/**
 * Conversion Utilities
 *
 * Utility functions for conversion state validation and decision making.
 * Provides centralized logic for determining when unit conversions are needed
 * and tracking conversion state across the application.
 *
 * This module specifically addresses the Container Yard Imperial conversion bug
 * where values would double (e.g., 4.921 ft → 16.145 ft) due to redundant conversions.
 *
 * @fileoverview Core conversion state management utilities
 * @version 1.0.0
 * @since August 2025
 */

import { unit as unitEnum } from '../enum/project';
import { logDebug } from './utils';

/** conversionSource written by InputForm onSubmit after it converted the Imperial form values to SI. */
export const FORM_SUBMIT_CONVERSION_SOURCE = 'InputForm onSubmit';

/**
 * Whether an Imperial project's projectInfo.data still holds display (Imperial) values, so the save must convert it.
 * conversionApplied=true is written both by useUnitConversion (SI → Imperial for display) and by InputForm onSubmit
 * (Imperial → SI for storage): only conversionSource tells the two states apart.
 */
export const holdsImperialDisplayValues = (project: { unit?: unitEnum; conversionApplied?: boolean; conversionSource?: string }): boolean =>
    project.unit === unitEnum.Imperial && project.conversionApplied === true && project.conversionSource !== FORM_SUBMIT_CONVERSION_SOURCE;

/**
 * Represents the result of a conversion necessity check
 *
 * Used to determine whether a unit conversion should be applied based on
 * current conversion state and target requirements.
 */
export interface ConversionDecision {
    /** Whether conversion is needed */
    conversionNeeded: boolean;
    /** Reason for the decision */
    reason: string;
    /** Source unit identified */
    sourceUnit: unitEnum;
    /** Target unit requested */
    targetUnit: unitEnum;
    /** Confidence level in the decision (0-1) */
    confidence: number;
}

/**
 * Determines if conversion is needed based on current state and target unit
 *
 * This is the core function for preventing double conversion bugs. It analyzes
 * the current conversion state and determines whether applying a conversion
 * would be appropriate or redundant.
 *
 * @param {unitEnum} targetUnit - The unit we want to convert TO
 * @param {unitEnum} currentUnit - The unit the data is currently in
 * @param {boolean} conversionApplied - Whether conversion has already been applied
 * @param {unitEnum} [originalUnit] - The original unit before any conversions (optional)
 * @returns {ConversionDecision} Decision object with conversion necessity and reasoning
 *
 * @example
 * // Check if Imperial data needs conversion to SI
 * const decision = isConversionNeeded(
 *   unitEnum.SI,        // target
 *   unitEnum.Imperial,  // current
 *   false,              // not yet converted
 *   unitEnum.Imperial   // original was Imperial
 * );
 * // decision.conversionNeeded === true
 *
 * @example
 * // Prevent double conversion (Container Yard bug scenario)
 * const decision = isConversionNeeded(
 *   unitEnum.SI,        // target
 *   unitEnum.Imperial,  // current (form display)
 *   true,               // already converted for display
 *   unitEnum.Imperial   // original was Imperial
 * );
 * // decision.conversionNeeded === false (prevents 4.921 ft → 16.145 ft bug)
 */
export function isConversionNeeded(targetUnit: unitEnum, currentUnit: unitEnum, conversionApplied = false, originalUnit?: unitEnum): ConversionDecision {
    // If units are the same, no conversion needed
    if (currentUnit === targetUnit) {
        return {
            conversionNeeded: false,
            reason: `Data already in target unit (${targetUnit})`,
            sourceUnit: currentUnit,
            targetUnit,
            confidence: 1.0,
        };
    }

    // If conversion has already been applied and we're trying to convert to the same target
    if (conversionApplied && originalUnit) {
        // Check if we're trying to convert back to original unit
        if (targetUnit === originalUnit) {
            return {
                conversionNeeded: true,
                reason: `Converting back to original unit (${originalUnit})`,
                sourceUnit: currentUnit,
                targetUnit,
                confidence: 0.9,
            };
        }

        // Check if conversion was already applied to reach target unit
        if (currentUnit === targetUnit) {
            return {
                conversionNeeded: false,
                reason: `Conversion already applied: ${originalUnit} → ${currentUnit}`,
                sourceUnit: currentUnit,
                targetUnit,
                confidence: 0.95,
            };
        }
    }

    // Default case: units are different and no previous conversion state detected
    return {
        conversionNeeded: true,
        reason: `Converting from ${currentUnit} to ${targetUnit}`,
        sourceUnit: currentUnit,
        targetUnit,
        confidence: 0.8,
    };
}

/**
 * Enhanced validation for conversion state with comprehensive logging
 *
 * @param conversionDecision - The conversion decision to log
 * @param context - Additional context for logging (e.g., 'setupFormForMethod')
 */
export function logConversionDecision(conversionDecision: ConversionDecision, context = 'ConversionUtils'): void {
    logDebug(context, 'Conversion Decision Analysis:', {
        conversionNeeded: conversionDecision.conversionNeeded,
        reason: conversionDecision.reason,
        sourceUnit: conversionDecision.sourceUnit,
        targetUnit: conversionDecision.targetUnit,
        confidence: conversionDecision.confidence,
        timestamp: new Date().toISOString(),
    });
}

/**
 * Validates the consistency of conversion state
 *
 * Performs comprehensive validation of conversion state to ensure data integrity
 * and prevent inconsistent conversion states that could lead to bugs like the
 * Container Yard Imperial conversion issue.
 *
 * @param {unitEnum} currentUnit - The unit the data is currently displayed in
 * @param {boolean} conversionApplied - Whether conversion has been applied to the data
 * @param {unitEnum} [originalUnit] - The original unit before any conversions
 * @returns {Object} Validation result with warnings and recommendations
 * @returns {boolean} returns.isValid - Whether the state is consistent
 * @returns {string[]} returns.warnings - Array of warning messages if inconsistencies found
 * @returns {string[]} returns.recommendations - Suggested actions to fix inconsistencies
 *
 * @example
 * // Valid state: data converted from Imperial to SI
 * const result = validateConversionState(
 *   unitEnum.SI,        // current
 *   true,               // conversion applied
 *   unitEnum.Imperial   // original
 * );
 * // result.isValid === true, result.warnings === []
 *
 * @example
 * // Invalid state: claims conversion applied but units match original
 * const result = validateConversionState(
 *   unitEnum.Imperial,  // current (same as original)
 *   true,               // claims conversion applied
 *   unitEnum.Imperial   // original
 * );
 * // result.isValid === false, result.warnings contains inconsistency warning
 */
export function validateConversionState(currentUnit: unitEnum, originalUnit?: unitEnum, conversionApplied?: boolean): string[] {
    const warnings: string[] = [];

    // Check for inconsistent state
    if (conversionApplied && !originalUnit) {
        warnings.push('Conversion marked as applied but no original unit tracked');
    }

    if (originalUnit && !conversionApplied && originalUnit !== currentUnit) {
        warnings.push(`Units differ (${originalUnit} → ${currentUnit}) but conversion not marked as applied`);
    }

    if (conversionApplied && originalUnit && originalUnit === currentUnit) {
        warnings.push('Conversion marked as applied but units are the same');
    }

    return warnings;
}
